import { type DropDownMenuItemProps, DropdownMenu } from '@/Components/filterbar/components/DropdownMenu';
import { useZnglyContext } from '@/Utility/context/ZnglyAppContext';
import { CircularProgress } from '@mui/material';
import React, { useCallback, useEffect, useMemo } from 'react';
import { z } from 'zod';
import { useFilterBarCtx } from '../FilterBar';
import { FilterBadge } from '../components/FilterBadge';
import { __useFilterState } from '../useFilterState';

const authorSearchSchema = z.object({
  author: z.array(z.string()).optional(),
});

const authorComponentProps = z.object({
  displayName: z.string().optional(),
});

type AuthorFilterProps = z.infer<typeof authorComponentProps>;

const AuthorFilter = ({ displayName = 'Author' }: AuthorFilterProps) => {
  const { props: ctxProps } = useFilterBarCtx();
  const { slowFilters, fastFilters, updateFilter, isUpdating } = __useFilterState(authorSearchSchema);

  const { backendData } = useZnglyContext();

  const handleAuthorToggle = useCallback(
    (authorId: string) => {
      updateFilter((prev) => {
        let newAuthors = [...(prev?.author || []), authorId];

        if (prev?.author?.includes?.(authorId)) {
          newAuthors = prev.author.filter((id) => id !== authorId);
        }

        return {
          author: newAuthors.length ? newAuthors : undefined,
        };
      });
    },
    [updateFilter]
  );

  // validate the filter authors are valid
  useEffect(() => {
    if (!backendData?.users?.length || !slowFilters?.author?.length) {
      return;
    }

    const allowedAuthorIds = backendData.users.map((user) => user.id);
    const validatedAuthors = slowFilters.author.filter((authorId) => allowedAuthorIds.includes(authorId));

    if (validatedAuthors.length !== slowFilters.author.length) {
      updateFilter({
        author: validatedAuthors.length ? validatedAuthors : undefined,
      });
    }
  }, [backendData?.users, slowFilters?.author]);

  // Create menu items - memoized with useMemo
  const menuItems: DropDownMenuItemProps[] = useMemo(() => {
    if (!backendData?.users) {
      return [
        {
          name: 'Loading...',
          icon: <CircularProgress size={25} />,
          disabled: true,
        },
      ];
    }

    return backendData.users.map(({ id, username, name }) => ({
      name: username || name,
      value: id,
      onClick: () => {
        handleAuthorToggle(id);
      },
    }));
  }, [backendData?.users, handleAuthorToggle]);

  return (
    <div style={{ position: 'relative' }}>
      <DropdownMenu
        className={ctxProps?.variant === 'default' ? 'z-btn-6' : 'z-btn-compact'}
        name={displayName}
        menuList={menuItems}
        type="multiple-select"
        options={{
          isSearchable: true,
          clearSearchOnClose: true,
          showCheckedOnTop: true,
          filterBar: slowFilters,
          filterBarKey: 'author',
        }}
      />
      <FilterBadge count={fastFilters?.author?.length || 0} isUpdating={isUpdating} />
    </div>
  );
};

const AuthorFilterMemo = React.memo(AuthorFilter);

export const FilterConfig_Author = {
  author: {
    searchSchema: authorSearchSchema,
    component: AuthorFilterMemo,
    componentProps: authorComponentProps,
  },
} as const;
